import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserDoctor, faStethoscope, faStar, faVideo, faCalendarCheck,
  faArrowRight, faCreditCard, faXmark, faSearch, faGlobe, faWallet, faFileMedical,
} from '@fortawesome/free-solid-svg-icons';
import BackButton from '../../components/BackButton/BackButton';
import teleconsultationService, {
  Specialization, Doctor, Slot, Appointment, TeleconsultChoices,
} from '../../services/teleconsultationService';
import walletService from '../../services/walletService';
import './TeleConsultation.css';

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtDateTime = (iso: string) => `${fmtDate(iso)}, ${fmtTime(iso)}`;

const STATUS_COLOR: Record<string, string> = {
  pending_payment: '#f59e0b',
  confirmed: '#10b981',
  in_progress: '#06b6d4',
  completed: '#64748b',
  cancelled: '#ef4444',
  no_show: '#ef4444',
};

/** Build a hidden form and POST to PayU (same approach as the gym flow). */
function submitPayuForm(payu: Record<string, string>, baseUrl: string) {
  const form = document.createElement('form');
  form.method = 'POST';
  // App convention: PAYU_BASE_URL is the host; the payment path is /_payment.
  // Append it unless it's already present (guards against double-append).
  form.action = baseUrl.endsWith('/_payment') ? baseUrl : `${baseUrl}/_payment`;
  Object.entries(payu).forEach(([k, v]) => {
    if (k === 'payu_base_url') return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = k;
    input.value = String(v);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

const TeleConsultation: React.FC = () => {
  const { t } = useTranslation('teleconsult');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState<'find' | 'appointments'>(() =>
    searchParams.get('tab') === 'appointments' ? 'appointments' : 'find'
  );
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);

  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [choices, setChoices] = useState<TeleconsultChoices | null>(null);
  const [selectedSpec, setSelectedSpec] = useState<number | null>(() => {
    const v = Number(searchParams.get('specialization'));
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  const [search, setSearch] = useState('');
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  // When the AI Family Doctor sends a patient here, the booking carries the
  // analysed report so the doctor sees the referral on their side.
  const reportId = useMemo(() => {
    const v = Number(searchParams.get('report_id'));
    return Number.isFinite(v) && v > 0 ? v : null;
  }, [searchParams]);
  const clearReportLink = () => {
    const p = new URLSearchParams(searchParams);
    p.delete('report_id');
    p.delete('specialization');
    setSearchParams(p, { replace: true });
    setSelectedSpec(null);
  };

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppts, setLoadingAppts] = useState(false);

  // Profile + booking modals
  const [profileDoctor, setProfileDoctor] = useState<Doctor | null>(null);
  const [bookingDoctor, setBookingDoctor] = useState<Doctor | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);

  // Rupee refund-wallet balance (to offer "pay from wallet")
  const [walletBalance, setWalletBalance] = useState(0);
  const loadWallet = useCallback(() => {
    walletService.getWallet().then((w) => setWalletBalance(w.balance)).catch(() => {});
  }, []);

  // Cancel modal (with refund-destination choice when the appointment is paid)
  const [cancelTarget, setCancelTarget] = useState<Appointment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelMode, setCancelMode] = useState<'wallet' | 'source'>('source');
  const [cancelling, setCancelling] = useState(false);

  // Reschedule modal (same or a different doctor)
  const [rsTarget, setRsTarget] = useState<Appointment | null>(null);
  const [rsChangeDoctor, setRsChangeDoctor] = useState(false);
  const [rsDoctorList, setRsDoctorList] = useState<Doctor[]>([]);
  const [rsDoctor, setRsDoctor] = useState<Doctor | null>(null); // whose slots we view (null = current doctor)
  const [rsSlots, setRsSlots] = useState<Slot[]>([]);
  const [rsLoadingSlots, setRsLoadingSlots] = useState(false);
  const [rsSelectedSlot, setRsSelectedSlot] = useState<Slot | null>(null);
  const [rsSelectedDay, setRsSelectedDay] = useState<string | null>(null);
  const [rsRefundMode, setRsRefundMode] = useState<'wallet' | 'source'>('wallet');
  const [rsBusy, setRsBusy] = useState(false);

  const showToast = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ msg, type });
    window.setTimeout(() => setToast(null), 4000);
  };

  // Payment return handling
  useEffect(() => {
    const p = searchParams.get('payment');
    const rs = searchParams.get('reschedule');
    if (!p && !rs) return;
    if (rs === 'success') showToast(t('toast.rescheduleSuccess'), 'success');
    else if (p === 'success') showToast(t('toast.paymentSuccess'), 'success');
    else if (p === 'failed') showToast(t('toast.paymentFailed'), 'error');
    else if (p) showToast(t('toast.paymentError'), 'error');
    setTab('appointments');
    searchParams.delete('payment');
    searchParams.delete('reschedule');
    searchParams.delete('appointment');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    teleconsultationService.getChoices().then(setChoices).catch(() => {});
    teleconsultationService.getSpecializations().then(setSpecializations).catch(() => {});
    loadWallet();
  }, [loadWallet]);

  const loadDoctors = useCallback(async () => {
    setLoadingDoctors(true);
    try {
      const list = await teleconsultationService.getDoctors({
        specialization: selectedSpec ?? undefined,
        search: search.trim() || undefined,
      });
      setDoctors(list);
    } catch {
      setDoctors([]);
    } finally {
      setLoadingDoctors(false);
    }
  }, [selectedSpec, search]);

  useEffect(() => {
    const t = window.setTimeout(loadDoctors, 250);
    return () => window.clearTimeout(t);
  }, [loadDoctors]);

  const loadAppointments = useCallback(async () => {
    setLoadingAppts(true);
    try {
      setAppointments(await teleconsultationService.getAppointments());
    } catch {
      setAppointments([]);
    } finally {
      setLoadingAppts(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'appointments') loadAppointments();
  }, [tab, loadAppointments]);

  // ── Booking ──────────────────────────────────────────────────────────────
  const openBooking = async (doctor: Doctor) => {
    setBookingDoctor(doctor);
    setSelectedSlot(null);
    setSelectedDay(null);
    setNotes('');
    setLoadingSlots(true);
    try {
      setSlots(await teleconsultationService.getDoctorSlots(doctor.id));
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Days that have at least one available slot (ordered).
  const days = useMemo(() => {
    const groups: Record<string, Slot[]> = {};
    slots.filter((s) => s.available).forEach((s) => {
      const day = fmtDate(s.start);
      (groups[day] = groups[day] || []).push(s);
    });
    return Object.entries(groups).map(([label, daySlots]) => ({
      label, slots: daySlots, date: new Date(daySlots[0].start),
    }));
  }, [slots]);

  // Default to the first available day whenever the day list changes.
  useEffect(() => {
    if (days.length && !days.some((d) => d.label === selectedDay)) {
      setSelectedDay(days[0].label);
    }
  }, [days, selectedDay]);

  // Selected day's slots grouped into Morning / Afternoon / Evening.
  const periods = useMemo(() => {
    const day = days.find((d) => d.label === selectedDay);
    const buckets: { key: string; label: string; slots: Slot[] }[] = [
      { key: 'morning', label: 'Morning', slots: [] },
      { key: 'afternoon', label: 'Afternoon', slots: [] },
      { key: 'evening', label: 'Evening', slots: [] },
    ];
    (day?.slots || []).forEach((s) => {
      const h = new Date(s.start).getHours();
      buckets[h < 12 ? 0 : h < 17 ? 1 : 2].slots.push(s);
    });
    return buckets.filter((b) => b.slots.length);
  }, [days, selectedDay]);

  const proceedToPay = async () => {
    if (!bookingDoctor || !selectedSlot) return;
    setBooking(true);
    try {
      const appt = await teleconsultationService.bookAppointment({
        doctor: bookingDoctor.id,
        scheduled_start: selectedSlot.start,
        mode: 'video',
        patient_notes: notes.trim() || undefined,
        report_id: reportId ?? undefined,
      });
      const pay = await teleconsultationService.initiatePayment(appt.id);
      submitPayuForm(pay.payu_data as unknown as Record<string, string>, pay.payu_data.payu_base_url);
      // browser now redirects to PayU
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error || t('toast.bookFailed');
      showToast(String(detail), 'error');
      setBooking(false);
    }
  };

  // Book the selected slot and pay it entirely from the wallet (no PayU redirect).
  const payBookingWithWallet = async () => {
    if (!bookingDoctor || !selectedSlot) return;
    setBooking(true);
    try {
      const appt = await teleconsultationService.bookAppointment({
        doctor: bookingDoctor.id,
        scheduled_start: selectedSlot.start,
        mode: 'video',
        patient_notes: notes.trim() || undefined,
        report_id: reportId ?? undefined,
      });
      await teleconsultationService.payWithWallet(appt.id);
      showToast(t('toast.walletPaid'), 'success');
      setBookingDoctor(null);
      loadWallet();
      setTab('appointments');
      loadAppointments();
    } catch (e: any) {
      const detail = e?.response?.data?.error || e?.response?.data?.detail || t('toast.bookFailed');
      showToast(String(detail), 'error');
    } finally {
      setBooking(false);
    }
  };

  // Pay an already-booked (pending) appointment from the wallet.
  const payExistingWithWallet = async (appt: Appointment) => {
    try {
      await teleconsultationService.payWithWallet(appt.id);
      showToast(t('toast.walletPaid'), 'success');
      loadWallet();
      loadAppointments();
    } catch (e: any) {
      const detail = e?.response?.data?.error || e?.response?.data?.detail || t('toast.paymentStartFailed');
      showToast(String(detail), 'error');
    }
  };

  // Book the selected slot, apply the wallet, and pay the remainder by card (redirect).
  const payBookingWithWalletPlusCard = async () => {
    if (!bookingDoctor || !selectedSlot) return;
    setBooking(true);
    try {
      const appt = await teleconsultationService.bookAppointment({
        doctor: bookingDoctor.id,
        scheduled_start: selectedSlot.start,
        mode: 'video',
        patient_notes: notes.trim() || undefined,
        report_id: reportId ?? undefined,
      });
      const pay = await teleconsultationService.initiatePartialWalletPayment(appt.id);
      submitPayuForm(pay.payu_data as unknown as Record<string, string>, pay.payu_data.payu_base_url);
      // browser redirects to PayU for the remaining card amount
    } catch (e: any) {
      const detail = e?.response?.data?.error || e?.response?.data?.detail || t('toast.bookFailed');
      showToast(String(detail), 'error');
      setBooking(false);
    }
  };

  // Pay an already-booked pending appointment with wallet + card (redirect).
  const payExistingWithWalletPlusCard = async (appt: Appointment) => {
    try {
      const pay = await teleconsultationService.initiatePartialWalletPayment(appt.id);
      submitPayuForm(pay.payu_data as unknown as Record<string, string>, pay.payu_data.payu_base_url);
    } catch (e: any) {
      const detail = e?.response?.data?.error || e?.response?.data?.detail || t('toast.paymentStartFailed');
      showToast(String(detail), 'error');
    }
  };

  // ── Appointment actions ──────────────────────────────────────────────────
  const joinConsult = async (appt: Appointment) => {
    try {
      // start (or re-join if already started) then go to the video room
      await teleconsultationService.startAppointment(appt.id);
      navigate(`/teleconsultation/room/${appt.id}`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || t('toast.startFailed');
      showToast(String(detail), 'error');
    }
  };

  const openCancel = (appt: Appointment) => {
    setCancelTarget(appt);
    setCancelReason('');
    setCancelMode('source'); // default to original payment method
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    const isPaid = cancelTarget.payment_status === 'paid';
    setCancelling(true);
    try {
      const res = await teleconsultationService.cancelAppointment(cancelTarget.id, {
        reason: cancelReason.trim() || undefined,
        refund_mode: isPaid ? cancelMode : undefined,
      });
      const detail = (res as any)?.refund_detail;
      showToast(detail || t('toast.cancelled'), 'success');
      setCancelTarget(null);
      loadAppointments();
    } catch {
      showToast(t('toast.cancelFailed'), 'error');
    } finally {
      setCancelling(false);
    }
  };

  // ── Reschedule ─────────────────────────────────────────────────────────────
  const loadRsSlots = useCallback(async (doctorId: number) => {
    setRsLoadingSlots(true);
    setRsSelectedSlot(null);
    setRsSelectedDay(null);
    try {
      setRsSlots(await teleconsultationService.getDoctorSlots(doctorId));
    } catch {
      setRsSlots([]);
    } finally {
      setRsLoadingSlots(false);
    }
  }, []);

  const openReschedule = (appt: Appointment) => {
    setRsTarget(appt);
    setRsChangeDoctor(false);
    setRsDoctor(null);
    setRsDoctorList([]);
    setRsRefundMode('wallet');
    loadRsSlots(appt.doctor);
  };

  // When "change doctor" is toggled on, fetch the directory once.
  useEffect(() => {
    if (rsChangeDoctor && rsDoctorList.length === 0) {
      teleconsultationService.getDoctors().then(setRsDoctorList).catch(() => {});
    }
  }, [rsChangeDoctor, rsDoctorList.length]);

  const rsPickDoctor = (d: Doctor) => {
    setRsDoctor(d);
    loadRsSlots(d.id);
  };

  const rsCurrentDoctorId = rsDoctor?.id ?? rsTarget?.doctor ?? 0;
  const rsIsDifferent = !!rsDoctor && !!rsTarget && rsDoctor.id !== rsTarget.doctor;
  const rsNewFee = rsDoctor ? Number(rsDoctor.consult_fee) : (rsTarget ? Number(rsTarget.fee_amount) : 0);
  const rsFeeDelta = rsTarget ? rsNewFee - Number(rsTarget.fee_amount) : 0;

  const confirmReschedule = async () => {
    if (!rsTarget || !rsSelectedSlot) return;
    setRsBusy(true);
    try {
      const { appointment, topUp } = await teleconsultationService.rescheduleAppointment(rsTarget.id, {
        scheduled_start: rsSelectedSlot.start,
        doctor: rsIsDifferent ? rsCurrentDoctorId : undefined,
        refund_mode: rsRefundMode,
      });
      if (topUp) {
        // Pricier doctor → redirect to PayU to collect the difference.
        submitPayuForm(topUp.payu_data as unknown as Record<string, string>, topUp.payu_data.payu_base_url);
        return; // browser redirects
      }
      const msg = appointment?.refund_detail
        ? `${t('toast.rescheduled')} ${appointment.refund_detail}`
        : t('toast.rescheduled');
      showToast(msg, 'success');
      setRsTarget(null);
      loadAppointments();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || t('toast.rescheduleFailed');
      showToast(String(detail), 'error');
    } finally {
      setRsBusy(false);
    }
  };

  // Reschedule slot grouping (mirrors the booking modal).
  const rsDays = useMemo(() => {
    const groups: Record<string, Slot[]> = {};
    rsSlots.filter((s) => s.available).forEach((s) => {
      const day = fmtDate(s.start);
      (groups[day] = groups[day] || []).push(s);
    });
    return Object.entries(groups).map(([label, daySlots]) => ({
      label, slots: daySlots, date: new Date(daySlots[0].start),
    }));
  }, [rsSlots]);

  useEffect(() => {
    if (rsDays.length && !rsDays.some((d) => d.label === rsSelectedDay)) {
      setRsSelectedDay(rsDays[0].label);
    }
  }, [rsDays, rsSelectedDay]);

  const rsPeriods = useMemo(() => {
    const day = rsDays.find((d) => d.label === rsSelectedDay);
    const buckets: { key: string; slots: Slot[] }[] = [
      { key: 'morning', slots: [] }, { key: 'afternoon', slots: [] }, { key: 'evening', slots: [] },
    ];
    (day?.slots || []).forEach((s) => {
      const h = new Date(s.start).getHours();
      buckets[h < 12 ? 0 : h < 17 ? 1 : 2].slots.push(s);
    });
    return buckets.filter((b) => b.slots.length);
  }, [rsDays, rsSelectedDay]);

  const canJoin = (a: Appointment) => {
    if (!(a.status === 'confirmed' || a.status === 'in_progress')) return false;
    const start = new Date(a.scheduled_start).getTime();
    const now = Date.now();
    // allow join from 10 min before until 60 min after start
    return now >= start - 10 * 60 * 1000 && now <= start + 60 * 60 * 1000;
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="tc-page">
      {toast && <div className={`tc-toast tc-toast-${toast.type}`}>{toast.msg}</div>}
      <section className="tc-welcome-banner">
        <Container>
          <BackButton />
          <div className="tc-welcome-layout">
            <div className="tc-welcome-text">
              <h1 className="tc-welcome-title">
                <FontAwesomeIcon icon={faUserDoctor} /> {t('title')}
              </h1>
              <p className="tc-welcome-subtitle">
                {t('subtitle')}
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Container className="tc-content">
      <div className="tc-tabs">
        <button className={tab === 'find' ? 'active' : ''} onClick={() => setTab('find')}>
          <FontAwesomeIcon icon={faStethoscope} /> {t('tabs.find')}
        </button>
        <button className={tab === 'appointments' ? 'active' : ''} onClick={() => setTab('appointments')}>
          <FontAwesomeIcon icon={faCalendarCheck} /> {t('tabs.appointments')}
        </button>
      </div>

      {tab === 'find' && (
        <>
          {reportId && (
            <div className="tc-report-banner">
              <FontAwesomeIcon icon={faFileMedical} />
              <span>{t('reportLinked')}</span>
              <button type="button" className="tc-report-clear" onClick={clearReportLink}>{t('reportUnlink')}</button>
            </div>
          )}
          <div className="tc-filters">
            <input
              className="tc-search"
              placeholder={t('searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="tc-chips">
              <button className={selectedSpec === null ? 'chip active' : 'chip'} onClick={() => setSelectedSpec(null)}>{t('all')}</button>
              {specializations.map((s) => (
                <button
                  key={s.id}
                  className={selectedSpec === s.id ? 'chip active' : 'chip'}
                  onClick={() => setSelectedSpec(s.id)}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>

          {loadingDoctors ? (
            <div className="tc-muted">{t('loadingDoctors')}</div>
          ) : doctors.length === 0 ? (
            <div className="tc-muted">{t('noDoctors')}</div>
          ) : (
            <div className="tc-doctor-grid">
              {doctors.map((d) => {
                const quals = (d.qualifications || '').split(/[,|]/).map((q) => q.trim()).filter(Boolean);
                return (
                  <div className="tc-doctor-card" key={d.id}>
                    <div className="tc-doctor-imgwrap">
                      {d.photo
                        ? <img src={d.photo} alt={d.full_name} className="tc-doctor-img" />
                        : <div className="tc-doctor-imgph"><FontAwesomeIcon icon={faUserDoctor} /></div>}
                      {Number(d.avg_rating) > 0
                        ? <span className="tc-img-badge"><FontAwesomeIcon icon={faStar} /> {Number(d.avg_rating).toFixed(1)}</span>
                        : <span className="tc-img-badge tc-img-badge-new">{t('new')}</span>}
                    </div>

                    <div className="tc-doctor-body">
                      <div className="tc-doctor-name">{t('drName', { name: d.full_name })}</div>
                      <div className="tc-doctor-spec">{d.specializations.map((s) => s.name).join(' | ') || t('generalPhysician')}</div>

                      {quals.length > 0 && (
                        <div className="tc-chip-row">
                          {quals.map((q, i) => <span className="tc-chip" key={i}>{q}</span>)}
                        </div>
                      )}

                      <div className="tc-stat-row">
                        <div className="tc-stat">
                          <div className="tc-stat-val"><FontAwesomeIcon icon={faCalendarCheck} /> {t('years', { count: d.experience_years || 0 })}</div>
                          <div className="tc-stat-lbl">{t('experience')}</div>
                        </div>
                        <div className="tc-stat">
                          <div className="tc-stat-val">{d.consult_fee_currency} {Number(d.consult_fee).toFixed(0)}</div>
                          <div className="tc-stat-lbl">{t('fees')}</div>
                        </div>
                      </div>

                      <div className="tc-doctor-actions">
                        <button className="tc-btn-outline" onClick={() => setProfileDoctor(d)}>{t('viewProfile')}</button>
                        <button className="tc-btn" onClick={() => openBooking(d)}><FontAwesomeIcon icon={faVideo} /> {t('book')}</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'appointments' && (
        loadingAppts ? (
          <div className="tc-muted">{t('loadingAppointments')}</div>
        ) : appointments.length === 0 ? (
          <div className="tc-muted">{t('noAppointments')}</div>
        ) : (
          <div className="tc-appt-list">
            {appointments.map((a) => {
              const stColor = STATUS_COLOR[a.status] || '#64748b';
              const stLabel = t(`status.${a.status}`, a.status);
              return (
                <div className="tc-appt-card" key={a.id}>
                  <div className="tc-appt-main">
                    <div className="tc-appt-doc">{a.doctor_name}</div>
                    <div className="tc-appt-when">{fmtDateTime(a.scheduled_start)} · {a.mode}</div>
                    {a.specialization_name && <div className="tc-appt-spec">{a.specialization_name}</div>}
                  </div>
                  <div className="tc-appt-side">
                    <span className="tc-status" style={{ color: stColor, borderColor: stColor }}>{stLabel}</span>
                    {a.status === 'cancelled' && a.refund_status && a.refund_status !== 'none' && (
                      <span className="tc-refund-badge">
                        {t(`refund.status.${a.refund_status}`, a.refund_status)}
                        {a.refund_mode ? ` · ${t(`refund.mode.${a.refund_mode}`, a.refund_mode)}` : ''}
                      </span>
                    )}
                    <div className="tc-appt-actions">
                      {a.status === 'pending_payment' && walletBalance >= Number(a.fee_amount) && (
                        <button className="tc-btn tc-btn-sm tc-btn-wallet" onClick={() => payExistingWithWallet(a)}>
                          <FontAwesomeIcon icon={faWallet} /> {t('payWithWalletShort')}
                        </button>
                      )}
                      {a.status === 'pending_payment' && walletBalance > 0 && walletBalance < Number(a.fee_amount) && (
                        <button className="tc-btn tc-btn-sm tc-btn-wallet" onClick={() => payExistingWithWalletPlusCard(a)}>
                          <FontAwesomeIcon icon={faWallet} /> {t('walletPlusCardShort')}
                        </button>
                      )}
                      {a.status === 'pending_payment' && (
                        <button className="tc-btn tc-btn-sm" onClick={async () => {
                          try {
                            const pay = await teleconsultationService.initiatePayment(a.id);
                            submitPayuForm(pay.payu_data as unknown as Record<string, string>, pay.payu_data.payu_base_url);
                          } catch { showToast(t('toast.paymentStartFailed'), 'error'); }
                        }}><FontAwesomeIcon icon={faCreditCard} /> {t('payNow')}</button>
                      )}
                      {canJoin(a) && (
                        <button className="tc-btn tc-btn-sm" onClick={() => joinConsult(a)}><FontAwesomeIcon icon={faVideo} /> {t('join')}</button>
                      )}
                      {(a.status === 'confirmed' || a.status === 'pending_payment') && (
                        <button className="tc-btn-outline tc-btn-sm" onClick={() => openReschedule(a)}>
                          <FontAwesomeIcon icon={faCalendarCheck} /> {t('reschedule')}
                        </button>
                      )}
                      {(a.status === 'confirmed' || a.status === 'pending_payment') && (
                        <button className="tc-btn-ghost tc-btn-sm" onClick={() => openCancel(a)}>{t('cancel')}</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Profile modal */}
      {profileDoctor && (
        <div className="tc-modal-overlay" onClick={() => setProfileDoctor(null)}>
          <div className="tc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="tc-modal-close" onClick={() => setProfileDoctor(null)}><FontAwesomeIcon icon={faXmark} /></button>
            <div className="tc-profile-head">
              {profileDoctor.photo
                ? <img src={profileDoctor.photo} alt={profileDoctor.full_name} className="tc-profile-photo" />
                : <div className="tc-profile-avatar">{profileDoctor.full_name.charAt(0)}</div>}
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0 }}>{t('drName', { name: profileDoctor.full_name })}</h2>
                <div className="tc-doctor-spec">{profileDoctor.specializations.map((s) => s.name).join(' | ') || t('generalPhysician')}</div>
                {profileDoctor.qualifications && <div className="tc-doctor-qual">{profileDoctor.qualifications}</div>}
              </div>
            </div>

            <div className="tc-profile-stats">
              <div className="tc-stat">
                <div className="tc-stat-val"><FontAwesomeIcon icon={faCalendarCheck} /> {t('years', { count: profileDoctor.experience_years || 0 })}</div>
                <div className="tc-stat-lbl">{t('experience')}</div>
              </div>
              <div className="tc-stat">
                <div className="tc-stat-val">{profileDoctor.consult_fee_currency} {Number(profileDoctor.consult_fee).toFixed(0)}</div>
                <div className="tc-stat-lbl">{t('profile.consultationFee')}</div>
              </div>
              {Number(profileDoctor.avg_rating) > 0 && (
                <div className="tc-stat">
                  <div className="tc-stat-val"><FontAwesomeIcon icon={faStar} /> {Number(profileDoctor.avg_rating).toFixed(1)}</div>
                  <div className="tc-stat-lbl">{t('profile.rating')}</div>
                </div>
              )}
            </div>

            {profileDoctor.bio && (
              <div className="tc-profile-section">
                <div className="tc-profile-section-title">{t('profile.about')}</div>
                <p className="tc-profile-text">{profileDoctor.bio}</p>
              </div>
            )}
            {profileDoctor.languages && (
              <div className="tc-profile-section">
                <div className="tc-profile-section-title">{t('profile.languages')}</div>
                <p className="tc-profile-text"><FontAwesomeIcon icon={faGlobe} /> {profileDoctor.languages}</p>
              </div>
            )}

            <button
              className="tc-btn tc-btn-block"
              onClick={() => { const d = profileDoctor; setProfileDoctor(null); openBooking(d); }}
            >
              <FontAwesomeIcon icon={faVideo} /> {t('profile.bookAppointment')}
            </button>
          </div>
        </div>
      )}

      {/* Booking modal */}
      {bookingDoctor && (
        <div className="tc-modal-overlay" onClick={() => !booking && setBookingDoctor(null)}>
          <div className="tc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="tc-modal-close" onClick={() => !booking && setBookingDoctor(null)}><FontAwesomeIcon icon={faXmark} /></button>
            <h2>{t('booking.bookWith', { name: bookingDoctor.full_name })}</h2>
            <div className="tc-modal-fee">{t('booking.consultationFee')} <b>{bookingDoctor.consult_fee_currency} {Number(bookingDoctor.consult_fee).toFixed(0)}</b></div>

            {loadingSlots ? (
              <div className="tc-muted">{t('booking.loadingSlots')}</div>
            ) : days.length === 0 ? (
              <div className="tc-muted">{t('booking.noSlots')}</div>
            ) : (
              <>
                <div className="tc-slot-section-label">{t('booking.selectDate')}</div>
                <div className="tc-date-row">
                  {days.map((d) => (
                    <button
                      key={d.label}
                      className={selectedDay === d.label ? 'tc-date-pill active' : 'tc-date-pill'}
                      onClick={() => { setSelectedDay(d.label); setSelectedSlot(null); }}
                    >
                      <span className="tc-date-dow">{d.date.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                      <span className="tc-date-dm">{d.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                      <span className="tc-date-count">{t('booking.slotsCount', { count: d.slots.length })}</span>
                    </button>
                  ))}
                </div>

                <div className="tc-slot-section-label">{t('booking.selectTime')}</div>
                <div className="tc-slots">
                  {periods.map((p) => (
                    <div key={p.key} className="tc-slot-day">
                      <div className="tc-slot-period-label">{t(`booking.${p.key}`)}</div>
                      <div className="tc-slot-grid">
                        {p.slots.map((s) => (
                          <button
                            key={s.start}
                            className={selectedSlot?.start === s.start ? 'tc-slot active' : 'tc-slot'}
                            onClick={() => setSelectedSlot(s)}
                          >
                            {fmtTime(s.start)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <textarea
              className="tc-notes"
              placeholder={t('booking.reasonPlaceholder')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />

            {selectedSlot && walletBalance >= Number(bookingDoctor.consult_fee) && (
              <>
                <button
                  className="tc-btn tc-btn-block tc-btn-wallet"
                  disabled={booking}
                  onClick={payBookingWithWallet}
                >
                  <FontAwesomeIcon icon={faWallet} /> {t('booking.payWithWallet', { fee: `${bookingDoctor.consult_fee_currency} ${Number(bookingDoctor.consult_fee).toFixed(0)}` })}
                </button>
                <div className="tc-wallet-hint">{t('booking.walletBalance', { balance: `${bookingDoctor.consult_fee_currency} ${walletBalance.toFixed(0)}` })}</div>
                <div className="tc-or-divider">{t('booking.or')}</div>
              </>
            )}
            {selectedSlot && walletBalance > 0 && walletBalance < Number(bookingDoctor.consult_fee) && (
              <>
                <button
                  className="tc-btn tc-btn-block tc-btn-wallet"
                  disabled={booking}
                  onClick={payBookingWithWalletPlusCard}
                >
                  <FontAwesomeIcon icon={faWallet} /> {t('booking.payWalletPlusCard', {
                    wallet: `${bookingDoctor.consult_fee_currency} ${walletBalance.toFixed(0)}`,
                    card: `${bookingDoctor.consult_fee_currency} ${(Number(bookingDoctor.consult_fee) - walletBalance).toFixed(0)}`,
                  })}
                </button>
                <div className="tc-or-divider">{t('booking.or')}</div>
              </>
            )}
            <button
              className="tc-btn tc-btn-block"
              disabled={!selectedSlot || booking}
              onClick={proceedToPay}
            >
              {booking ? t('booking.redirecting') : selectedSlot
                ? <><FontAwesomeIcon icon={faCreditCard} /> {t('booking.proceedToPay', { fee: `${bookingDoctor.consult_fee_currency} ${Number(bookingDoctor.consult_fee).toFixed(0)}` })}</>
                : t('booking.selectSlot')}
            </button>
          </div>
        </div>
      )}

      {/* Cancel modal — with refund-destination choice when paid */}
      {cancelTarget && (
        <div className="tc-modal-overlay" onClick={() => !cancelling && setCancelTarget(null)}>
          <div className="tc-modal tc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <button className="tc-modal-close" onClick={() => !cancelling && setCancelTarget(null)}><FontAwesomeIcon icon={faXmark} /></button>
            <h2>{t('cancelModal.title')}</h2>
            <p className="tc-muted">{t('cancelModal.subtitle', { doctor: cancelTarget.doctor_name, when: fmtDateTime(cancelTarget.scheduled_start) })}</p>

            {cancelTarget.payment_status === 'paid' && (
              <>
                <div className="tc-slot-section-label">{t('cancelModal.refundTo')}</div>
                <div className="tc-refund-options">
                  {(choices?.refund_modes || [{value: 'source', label: 'To source (PayU)'}, {value: 'wallet', label: 'To wallet'}]).map(m => (
                    <label key={m.value} className={cancelMode === m.value ? 'tc-refund-opt active' : 'tc-refund-opt'}>
                      <input type="radio" name="refundMode" checked={cancelMode === m.value} onChange={() => setCancelMode(m.value as any)} />
                      <div>
                        <div className="tc-refund-opt-title">{t(`refund.mode.${m.value}`, m.label)}</div>
                        <div className="tc-refund-opt-desc">{t(`cancelModal.${m.value}Desc`, '')}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="tc-refund-amount">{t('cancelModal.refundAmount', { amount: `${cancelTarget.currency} ${Number(cancelTarget.fee_amount).toFixed(0)}` })}</div>
              </>
            )}

            <textarea
              className="tc-notes"
              placeholder={t('cancelModal.reasonPlaceholder')}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={2}
            />
            <div className="tc-modal-actions">
              <button className="tc-btn-ghost" disabled={cancelling} onClick={() => setCancelTarget(null)}>{t('cancelModal.keep')}</button>
              <button className="tc-btn tc-btn-danger" disabled={cancelling} onClick={confirmCancel}>
                {cancelling ? t('cancelModal.cancelling') : t('cancelModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule modal — same or different doctor */}
      {rsTarget && (
        <div className="tc-modal-overlay" onClick={() => !rsBusy && setRsTarget(null)}>
          <div className="tc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="tc-modal-close" onClick={() => !rsBusy && setRsTarget(null)}><FontAwesomeIcon icon={faXmark} /></button>
            <h2>{t('rescheduleModal.title')}</h2>
            <p className="tc-muted">{t('rescheduleModal.current', { doctor: rsTarget.doctor_name, when: fmtDateTime(rsTarget.scheduled_start) })}</p>

            <label className="tc-change-doctor">
              <input type="checkbox" checked={rsChangeDoctor} onChange={(e) => {
                setRsChangeDoctor(e.target.checked);
                if (!e.target.checked) { setRsDoctor(null); if (rsTarget) loadRsSlots(rsTarget.doctor); }
              }} />
              {t('rescheduleModal.changeDoctor')}
            </label>

            {rsChangeDoctor && (
              <div className="tc-rs-doctor-row">
                {rsDoctorList.map((d) => (
                  <button
                    key={d.id}
                    className={rsCurrentDoctorId === d.id ? 'tc-date-pill active' : 'tc-date-pill'}
                    onClick={() => rsPickDoctor(d)}
                  >
                    <span className="tc-date-dow">{d.full_name}</span>
                    <span className="tc-date-count">{d.consult_fee_currency} {Number(d.consult_fee).toFixed(0)}</span>
                  </button>
                ))}
              </div>
            )}

            {rsIsDifferent && rsFeeDelta !== 0 && (
              <div className={rsFeeDelta > 0 ? 'tc-fee-note tc-fee-up' : 'tc-fee-note tc-fee-down'}>
                {rsFeeDelta > 0
                  ? t('rescheduleModal.topUpNote', { amount: `${rsTarget.currency} ${Math.abs(rsFeeDelta).toFixed(0)}` })
                  : t('rescheduleModal.refundNote', { amount: `${rsTarget.currency} ${Math.abs(rsFeeDelta).toFixed(0)}` })}
              </div>
            )}

            {/* When a cheaper doctor yields an excess refund, let the patient choose destination */}
            {rsIsDifferent && rsFeeDelta < 0 && rsTarget.payment_status === 'paid' && (
              <div className="tc-refund-inline">
                <span>{t('rescheduleModal.refundExcessTo')}</span>
                {(choices?.refund_modes || [{value: 'wallet', label: 'To wallet'}, {value: 'source', label: 'To source (PayU)'}]).map(m => (
                  <button key={m.value} className={rsRefundMode === m.value ? 'tc-mini active' : 'tc-mini'} onClick={() => setRsRefundMode(m.value as any)}>
                    {t(`refund.mode.${m.value}`, m.label)}
                  </button>
                ))}
              </div>
            )}

            {rsLoadingSlots ? (
              <div className="tc-muted">{t('booking.loadingSlots')}</div>
            ) : rsDays.length === 0 ? (
              <div className="tc-muted">{t('booking.noSlots')}</div>
            ) : (
              <>
                <div className="tc-slot-section-label">{t('booking.selectDate')}</div>
                <div className="tc-date-row">
                  {rsDays.map((d) => (
                    <button
                      key={d.label}
                      className={rsSelectedDay === d.label ? 'tc-date-pill active' : 'tc-date-pill'}
                      onClick={() => { setRsSelectedDay(d.label); setRsSelectedSlot(null); }}
                    >
                      <span className="tc-date-dow">{d.date.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                      <span className="tc-date-dm">{d.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                      <span className="tc-date-count">{t('booking.slotsCount', { count: d.slots.length })}</span>
                    </button>
                  ))}
                </div>
                <div className="tc-slot-section-label">{t('booking.selectTime')}</div>
                <div className="tc-slots">
                  {rsPeriods.map((p) => (
                    <div key={p.key} className="tc-slot-day">
                      <div className="tc-slot-period-label">{t(`booking.${p.key}`)}</div>
                      <div className="tc-slot-grid">
                        {p.slots.map((s) => (
                          <button
                            key={s.start}
                            className={rsSelectedSlot?.start === s.start ? 'tc-slot active' : 'tc-slot'}
                            onClick={() => setRsSelectedSlot(s)}
                          >
                            {fmtTime(s.start)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <button
              className="tc-btn tc-btn-block"
              disabled={!rsSelectedSlot || rsBusy}
              onClick={confirmReschedule}
            >
              {rsBusy ? t('rescheduleModal.working')
                : rsIsDifferent && rsFeeDelta > 0
                  ? <><FontAwesomeIcon icon={faCreditCard} /> {t('rescheduleModal.payTopUp', { amount: `${rsTarget.currency} ${rsFeeDelta.toFixed(0)}` })}</>
                  : t('rescheduleModal.confirm')}
            </button>
          </div>
        </div>
      )}
      </Container>
    </div>
  );
};

export default TeleConsultation;

